import { Tooltip } from '@equinor/eds-core-react';
import { theme } from '../../../../tokens/theme';
import { ArchelMeta, Proportions } from '../modelAreaCoordinates.utils';
import * as Styled from './ArchelProportions.styled';

interface ArchelProportionsProps {
  wholeModel: Proportions | undefined;
  selected: Proportions | undefined;
  loading: boolean;
  archels: ArchelMeta[];
}

export const ArchelProportions = ({
  wholeModel,
  selected,
  loading,
  archels,
}: ArchelProportionsProps) => {
  if (!wholeModel) {
    return (
      <Styled.Panel>
        <Styled.PanelTitle>Archel proportions</Styled.PanelTitle>
        <Styled.HintText>Loading model proportions…</Styled.HintText>
      </Styled.Panel>
    );
  }

  const visibleArchels = archels.filter(
    (a) => !isNaN(wholeModel[a.id]) && wholeModel[a.id] !== undefined,
  );
  const twoCol = visibleArchels.length > 9;

  return (
    <Styled.Panel $twoCol={twoCol}>
      <Styled.PanelTitle>Archel proportions</Styled.PanelTitle>

      <Styled.ArchelList $twoCol={twoCol}>
        {visibleArchels.map((archel) => {
          const whole = wholeModel[archel.id];
          const sel = selected?.[archel.id] ?? null;
          const diff = sel !== null ? sel - whole : null;
          const pctWhole = whole * 100;
          const pctSel = sel !== null ? sel * 100 : 0;
          const diffText =
            diff !== null
              ? `${diff > 0 ? '+' : ''}${(diff * 100).toFixed(1)}pp`
              : null;

          return (
            <Styled.ArchelRow key={archel.id}>
              <Styled.ArchelLabelRow>
                <Styled.ColorSwatch $color={archel.color} />
                <Styled.ArchelName>{archel.name}</Styled.ArchelName>
                {diff !== null && !loading ? (
                  <Tooltip
                    title={`${diffText} compared to whole model`}
                    placement="top"
                  >
                    <Styled.DiffLabel
                      $positive={(diff ?? 0) >= 0}
                      $neutral={Math.abs(diff) < 0.005}
                    >
                      {diffText}
                    </Styled.DiffLabel>
                  </Tooltip>
                ) : (
                  <Styled.DiffLabel
                    $positive={false}
                    $neutral={true}
                    style={{ visibility: 'hidden' }}
                  >
                    +0.0pp
                  </Styled.DiffLabel>
                )}
              </Styled.ArchelLabelRow>

              <Styled.BarTrack $dimmed={false} title="Whole model">
                <Styled.BarFill $pct={pctWhole} $color="#9e9e9e" />
                <Styled.BarPct>{(whole * 100).toFixed(1)}%</Styled.BarPct>
              </Styled.BarTrack>

              <Styled.BarTrack
                $dimmed={loading || sel === null}
                title="Selected area"
              >
                <Styled.BarFill $pct={pctSel} $color={archel.color} />
                {sel !== null && !loading && (
                  <Styled.BarPct>{(sel * 100).toFixed(1)}%</Styled.BarPct>
                )}
              </Styled.BarTrack>
            </Styled.ArchelRow>
          );
        })}
      </Styled.ArchelList>

      <Styled.Legend>
        <Styled.LegendItem>
          <Styled.LegendBarTrack>
            <Styled.LegendBarFill $color="#9e9e9e" />
          </Styled.LegendBarTrack>
          <Styled.LegendLabel>Whole model</Styled.LegendLabel>
        </Styled.LegendItem>
        <Styled.LegendItem>
          <Styled.LegendBarTrack>
            <Styled.LegendBarFill $color={theme.light.primary.resting} />
          </Styled.LegendBarTrack>
          <Styled.LegendLabel>Selected area</Styled.LegendLabel>
        </Styled.LegendItem>
      </Styled.Legend>
    </Styled.Panel>
  );
};
